import { Separator, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { BadgeBlock } from "./blocks/BadgeBlock";
import { CalloutBlock } from "./blocks/CalloutBlock";
import { CardBlock } from "./blocks/CardBlock";
import { ChartBlock } from "./blocks/ChartBlock";
import { ChecklistItemBlock } from "./blocks/ChecklistItemBlock";
import { ExpandableBlock } from "./blocks/ExpandableBlock";
import { FilePreviewBlock } from "./blocks/FilePreviewBlock";
import { GridBlock } from "./blocks/GridBlock";
import { HeadingBlock } from "./blocks/HeadingBlock";
import { ListBlock } from "./blocks/ListBlock";
import { MetricBlock } from "./blocks/MetricBlock";
import { ParagraphBlock } from "./blocks/ParagraphBlock";
import { SectionBlock } from "./blocks/SectionBlock";
import { SourceLinkBlock } from "./blocks/SourceLinkBlock";
import { TableBlock } from "./blocks/TableBlock";
import { VideoPreviewBlock } from "./blocks/VideoPreviewBlock";
import { type CanvasBlock } from "./types";

interface CanvasBlockRendererProps {
  block: CanvasBlock;
  onSectionClick?: (sectionId: string, title: string) => void;
  onBadgeAction?: (action: string, label: string) => void;
}

export function CanvasBlockRenderer({
  block,
  onSectionClick,
  onBadgeAction,
}: CanvasBlockRendererProps) {
  switch (block.type) {
    case "heading":
      return <HeadingBlock level={block.level} text={block.text} />;

    case "paragraph":
      return <ParagraphBlock text={block.text} />;

    case "badge":
      return (
        // eslint-disable-next-line mg-local/require-testid -- BadgeBlock renders data-testid="badge-block-button" internally when onClick is set
        <BadgeBlock
          label={block.label}
          color={block.color}
          size={block.size}
          onClick={
            block.action && onBadgeAction
              ? () => onBadgeAction(block.action!, block.label)
              : undefined
          }
        />
      );

    case "card":
      return (
        <CardBlock
          title={block.title}
          subtitle={block.subtitle}
          badge={block.badge}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        >
          {block.children}
        </CardBlock>
      );

    case "grid":
      return (
        <GridBlock
          columns={block.columns}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        >
          {block.children}
        </GridBlock>
      );

    case "table":
      return (
        <TableBlock
          headers={block.headers}
          rows={block.rows}
          highlightRules={block.highlightRules}
        />
      );

    case "list":
      return (
        <ListBlock
          ordered={block.ordered}
          items={block.items}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        />
      );

    case "divider":
      return <Separator size="4" data-testid="divider-block" />;

    case "metric":
      return (
        <MetricBlock
          label={block.label}
          value={block.value}
          trend={block.trend}
        />
      );

    case "expandable":
      return (
        <ExpandableBlock
          title={block.title}
          summary={block.summary}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        >
          {block.children}
        </ExpandableBlock>
      );

    case "section":
      return (
        <SectionBlock
          id={block.id}
          title={block.title}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        >
          {block.children}
        </SectionBlock>
      );

    case "source_link":
      return (
        <SourceLinkBlock
          title={block.title}
          url={block.url}
          linkType={block.linkType}
        />
      );

    case "chart":
      return <ChartBlock chartType={block.chartType} data={block.data} />;

    case "callout":
      return <CalloutBlock variant={block.variant} text={block.text} />;

    case "file_preview":
      return (
        <FilePreviewBlock
          url={block.url}
          fileName={block.fileName}
          fileType={block.fileType}
          pageCount={block.pageCount}
        />
      );

    case "video_preview":
      return <VideoPreviewBlock url={block.url} fileName={block.fileName} />;

    case "checklist_item":
      return (
        <ChecklistItemBlock
          criterion={block.criterion}
          status={block.status}
          finding={block.finding}
        />
      );

    default:
      // why NOT `return null`: a canvas block the renderer does not recognise
      // used to disappear with no trace, on a surface that carries compliance
      // content. A reviewer cannot tell "the model said nothing about X" from
      // "the block about X failed to render", and the second is the one that
      // needs reporting. `blocks` is typed `z.array(z.record(z.unknown()))` in
      // the wire schema, so nothing upstream rejects an unknown type either.
      //
      // why-not gate rendering on askPunttCanvasBlockSchema: it knows 15 types
      // and this renderer draws 17, and a badge whose `color` sits outside the
      // enum renders fine today. Validating here would turn drawable blocks
      // into fallbacks - a regression. The schema is aligned to the renderer
      // instead, and canvasBlockTypesMatchRenderer pins the two together.
      // The switch is exhaustive over CanvasBlock, so TypeScript narrows
      // `block` to `never` here. The WIRE disagrees: canvasContent.blocks is
      // `z.array(z.record(z.unknown()))`, so a block type the FE has never
      // heard of does reach this line at runtime. Read the discriminant back
      // through a minimal shape rather than trusting the narrowing.
      return (
        <UnsupportedBlock
          type={String((block as { type?: unknown }).type ?? "")}
        />
      );
  }
}

/** A block the renderer has no case for. Visible on purpose: silence is
 * indistinguishable from the model having omitted the content. */
function UnsupportedBlock({ type }: { type: string }) {
  const { t } = useTranslation("ui");
  return (
    <div
      role="note"
      data-testid={`canvas-unsupported-block-${type || "unknown"}`}
      aria-label={t("canvasUnsupportedBlock")}
      className="my-2 rounded-lg border border-dashed border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 px-3 py-2"
    >
      <Text as="span" size="1" className="text-puntt-neutral-gray-11">
        {t("canvasUnsupportedBlock")}
        {type ? ` (${type})` : ""}
      </Text>
    </div>
  );
}
